import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BadgeCheck, ChevronDown, ChevronRight, Circle, SquareCheckBig, X } from 'lucide-react'
import { api } from './api'
import { h5Copy } from './copy'
import { useStoreSelection } from './useStoreSelection'
import {
  decorateReservationTables,
  preferredReservationTableId,
  reservationAreaName,
  type DecoratedReservationTable,
  type ReservationDesk,
  type ReservationMemberProfile,
  type ReservedMember
} from './reservation'

type ReservationAction =
  | { kind: 'reserve'; tableId: string }
  | { kind: 'queue'; tableId: string }
  | { kind: 'cancel-reservation'; id: string }
  | { kind: 'cancel-queue'; id: string }

const PREVIEW_MEMBER_COUNT = 5

export default function ReservationPage() {
  const { store, setStore, stores, canSwitchStore } = useStoreSelection()
  const queryClient = useQueryClient()
  const [selectedId, setSelectedId] = useState('')
  const [expandedMembers, setExpandedMembers] = useState<Record<string, boolean>>({})
  const [profileTarget, setProfileTarget] = useState<{ table: DecoratedReservationTable; member: ReservedMember } | null>(null)
  const [toast, setToast] = useState('')

  const desk = useQuery({
    queryKey: ['reservation-desk', store?.id],
    queryFn: () => api<ReservationDesk>(`/api/h5/texas/tables?storeId=${encodeURIComponent(store!.id)}`),
    enabled: !!store,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false
  })
  const tables = useMemo(() => decorateReservationTables(desk.data?.tables), [desk.data?.tables])

  useEffect(() => {
    const nextId = preferredReservationTableId(tables, selectedId, desk.data?.myReservation, desk.data?.myQueue)
    if (nextId !== selectedId) setSelectedId(nextId)
  }, [tables, selectedId, desk.data?.myReservation, desk.data?.myQueue])

  useEffect(() => {
    if (!toast) return
    const timer = window.setTimeout(() => setToast(''), 1800)
    return () => window.clearTimeout(timer)
  }, [toast])

  const action = useMutation({
    mutationFn: (target: ReservationAction) => {
      if (!store) return Promise.reject(new Error('请选择门店'))
      if (target.kind === 'reserve') {
        return api<ReservationDesk>(`/api/h5/texas/tables/${target.tableId}/reserve`, {
          method: 'POST', body: JSON.stringify({ storeId: store.id })
        })
      }
      if (target.kind === 'queue') {
        return api<ReservationDesk>('/api/h5/texas/queue', {
          method: 'POST', body: JSON.stringify({ storeId: store.id, tableId: target.tableId })
        })
      }
      const path = target.kind === 'cancel-reservation'
        ? `/api/h5/texas/reservations/${target.id}/cancel`
        : `/api/h5/texas/queue/${target.id}/cancel`
      return api<ReservationDesk>(path, { method: 'POST' })
    },
    onSuccess: (data, target) => {
      queryClient.setQueryData(['reservation-desk', store?.id], data)
      setToast(target.kind === 'reserve' ? '预约成功' : target.kind === 'queue' ? '已加入排队' : target.kind === 'cancel-reservation' ? '已取消预约' : '已取消排队')
    },
    onError: (error) => setToast(error instanceof Error ? error.message : '操作失败')
  })

  const hasActiveRecord = !!desk.data?.myReservation || !!desk.data?.myQueue
  const reservationTable = tables.find((table) => table.id === desk.data?.myReservation?.tableId)

  return <main className={`reservation-page ${hasActiveRecord ? 'has-reservation-status' : ''}`}>
    <header className="reservation-header">
      <div><div className="eyebrow">上头鱼 · SHANG TOU YU BAR</div><h1>预约</h1></div>
      {canSwitchStore && <label className="store-select"><select aria-label="选择门店" value={store?.id || ''} onChange={(event) => setStore(stores.find((item) => item.id === event.target.value) || null)}>{stores.map((item) => <option key={item.id} value={item.id}>{h5Copy(item.name)}</option>)}</select><ChevronDown /></label>}
    </header>
    <section className="reservation-reward"><span>预约礼</span><strong>预约到店即可获赠 3,000 鱼票</strong></section>

    <section className="reservation-content">
      {desk.isLoading && <ReservationState text="活动区域加载中…" />}
      {desk.isError && <ReservationState error text="加载失败，点击重试" onClick={() => desk.refetch()} />}
      {!desk.isLoading && !desk.isError && !tables.length && <ReservationState text="当前门店暂无活动" />}
      {tables.map((table) => <ReservationCard
        key={table.id}
        table={table}
        selected={table.id === selectedId}
        hasActiveRecord={hasActiveRecord}
        submitting={action.isPending}
        membersExpanded={!!expandedMembers[table.id]}
        onSelect={() => setSelectedId(table.id)}
        onReserve={() => action.mutate({ kind: 'reserve', tableId: table.id })}
        onQueue={() => action.mutate({ kind: 'queue', tableId: table.id })}
        onToggleMembers={() => setExpandedMembers((current) => ({ ...current, [table.id]: !current[table.id] }))}
        onMember={(member) => setProfileTarget({ table, member })}
      />)}

      {!!tables.length && <div className="reservation-arrival-tip"><SquareCheckBig /><span>到店后请向工作人员出示预约记录</span></div>}
    </section>

    {desk.data?.myReservation && <section className="reservation-status-bar">
      <strong>已预约 <em>{reservationTable?.areaName || reservationAreaName({ name: desk.data.myReservation.tableName, tableNo: desk.data.myReservation.tableNo || '' })}</em></strong>
      {desk.data.myReservation.status === 'RESERVED'
        ? <button disabled={action.isPending} onClick={() => action.mutate({ kind: 'cancel-reservation', id: desk.data!.myReservation!.id })}>取消预约</button>
        : <button disabled>已到店</button>}
    </section>}
    {!desk.data?.myReservation && desk.data?.myQueue && <section className="reservation-status-bar">
      <strong>排队中 <em>第{desk.data.myQueue.rankNo}位</em></strong>
      <button disabled={action.isPending} onClick={() => action.mutate({ kind: 'cancel-queue', id: desk.data!.myQueue!.id })}>取消排队</button>
    </section>}

    {toast && <div className="reservation-toast" role="status"><BadgeCheck />{toast}</div>}
    {profileTarget && <MemberProfileSheet target={profileTarget} onClose={() => setProfileTarget(null)} />}
  </main>
}

function ReservationCard({ table, selected, hasActiveRecord, submitting, membersExpanded, onSelect, onReserve, onQueue, onToggleMembers, onMember }: {
  table: DecoratedReservationTable
  selected: boolean
  hasActiveRecord: boolean
  submitting: boolean
  membersExpanded: boolean
  onSelect: () => void
  onReserve: () => void
  onQueue: () => void
  onToggleMembers: () => void
  onMember: (member: ReservedMember) => void
}) {
  const members = table.reservedMembers || []
  const displayedMembers = membersExpanded ? members : members.slice(0, PREVIEW_MEMBER_COUNT)
  const queueText = table.unavailable ? '当前暂不可预约' : table.hasCapacity ? '当前无需排队' : `前方 ${table.queueCount} 人`
  return <article
    className={`reservation-card ${selected ? 'selected' : 'compact'}`}
    onClick={onSelect}
    onKeyDown={(event) => { if (!selected && ['Enter', ' '].includes(event.key)) { event.preventDefault(); onSelect() } }}
    role={selected ? undefined : 'button'}
    tabIndex={selected ? undefined : 0}
  >
    <header>
      <h2>{table.areaName}</h2>
      <span className={`reservation-availability ${table.unavailable ? 'unavailable' : ''}`}><Circle />{table.unavailable ? '暂停预约' : table.hasCapacity ? '可预约' : '需排队'}</span>
    </header>

    {selected ? <>
      <div className="reservation-card-action-row">
        <div><strong>剩余名额 <em>{table.remaining}</em></strong><span>{queueText}</span></div>
        {!hasActiveRecord && table.hasCapacity && <button disabled={submitting} onClick={(event) => { event.stopPropagation(); onReserve() }}>{submitting ? '提交中…' : '预约'}</button>}
        {!hasActiveRecord && table.queueable && <button disabled={submitting || table.unavailable} onClick={(event) => { event.stopPropagation(); onQueue() }}>{submitting ? '提交中…' : '排队'}</button>}
      </div>
      <div className="reservation-members">
        <div className="reservation-members-title"><strong>已预约成员 <em>{table.occupiedSeats}人</em></strong>{members.length > PREVIEW_MEMBER_COUNT && <button onClick={(event) => { event.stopPropagation(); onToggleMembers() }}>{membersExpanded ? '收起' : '查看全部'} <ChevronRight /></button>}</div>
        {displayedMembers.length ? <div className="reservation-member-grid">{displayedMembers.map((member) => <button key={member.memberId} onClick={(event) => { event.stopPropagation(); onMember(member) }}>
          <MemberAvatar member={member} /><span>{member.nickname || '会员'}</span>
        </button>)}</div> : <p>还没有成员预约，欢迎成为第一位</p>}
      </div>
    </> : <div className="reservation-compact-row">
      <span>{table.hasCapacity ? `剩余名额 ${table.remaining}` : queueText}</span>
      <div className="reservation-compact-members">{members.slice(0, 3).map((member) => <MemberAvatar key={member.memberId} member={member} />)}</div>
      <strong>{table.occupiedSeats}人已预约</strong><ChevronRight />
    </div>}
  </article>
}

function MemberAvatar({ member }: { member: ReservedMember }) {
  return <span className="reservation-member-avatar">{member.avatarUrl ? <img src={member.avatarUrl} alt="" /> : (member.nickname || '会').slice(0, 1)}</span>
}

function MemberProfileSheet({ target, onClose }: { target: { table: DecoratedReservationTable; member: ReservedMember }; onClose: () => void }) {
  const profile = useQuery({
    queryKey: ['reservation-member-profile', target.table.id, target.member.memberId],
    queryFn: () => api<ReservationMemberProfile>(`/api/h5/texas/tables/${target.table.id}/members/${target.member.memberId}/profile`),
    staleTime: 60_000
  })
  const data = profile.data
  return <div className="reservation-profile-mask" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}>
    <section className="reservation-profile-sheet" role="dialog" aria-modal="true" aria-labelledby="reservation-profile-title">
      <div className="reservation-profile-handle" />
      <header><h2 id="reservation-profile-title">会员资料</h2><button aria-label="关闭" onClick={onClose}><X /></button></header>
      <div className="reservation-profile-summary">
        <MemberAvatar member={{ ...target.member, nickname: data?.nickname || target.member.nickname, avatarUrl: data?.avatarUrl || target.member.avatarUrl }} />
        <div><strong>{data?.nickname || target.member.nickname || '会员'}</strong><p><span>已预约</span>{data?.areaName || target.table.areaName}</p></div>
      </div>
      {profile.isLoading && <ReservationState text="会员资料加载中…" />}
      {profile.isError && <ReservationState error text="会员资料加载失败，点击重试" onClick={() => profile.refetch()} />}
      {data && <div className="reservation-profile-rows">
        <div><span>会员等级</span><strong className="reservation-level"><i>V{data.levelCode || 0}</i>{data.levelName || '新客'}</strong></div>
        <div><span>鱼票余额</span><strong>{Number(data.pointsBalance || 0).toLocaleString('en-US')} <small>鱼票</small></strong></div>
        <div><span>累计存入</span><strong>{Number(data.totalDepositPoints || 0).toLocaleString('en-US')} <small>鱼票</small></strong></div>
      </div>}
    </section>
  </div>
}

function ReservationState({ text, error, onClick }: { text: string; error?: boolean; onClick?: () => void }) {
  return <button className={`reservation-state ${error ? 'error' : ''}`} onClick={onClick} disabled={!onClick}>{text}</button>
}
